import React from 'react';
import PropTypes from 'prop-types';
import IconButton from 'material-ui/IconButton';
import EditIcon from 'material-ui/svg-icons/editor/mode-edit';
import AddIcon from 'material-ui/svg-icons/content/add';
import OverlayController from "../overlay/OverlayController";
import QueueDialog from "../queue/QueueDialog";
import utils from '../../utils/utils';
import _ from 'lodash';


export default class QueuesPanel extends React.Component {

    static propTypes = {
        project: PropTypes.object.isRequired,
        queues: PropTypes.array.isRequired,
        selection: PropTypes.object,
        delegate: PropTypes.shape({
            queuesPanelDidHighlightQueue: PropTypes.func.isRequired,
            queuesPanelDidClearHighlight: PropTypes.func.isRequired,
            queuesPanelDidSelectQueue: PropTypes.func.isRequired,
            queuesPanelDidCreateQueue: PropTypes.func.isRequired
        }).isRequired
    };

    static defaultProps = {};

    constructor(props) {
        super(props);
        this.updateProps(props);
        this.onQueueMouseEnter = this.onQueueMouseEnter.bind(this);
        this.onQueueMouseLeave = this.onQueueMouseLeave.bind(this);
        this.onQueueClick = this.onQueueClick.bind(this);
        this.onQueueEdit = this.onQueueEdit.bind(this);
        this.onQueueCreate = this.onQueueCreate.bind(this);
        this.state = {};
    }

    componentWillReceiveProps(nextProps) {
        this.updateProps(nextProps);
    }

    updateProps(props) {
        this.boardsMap = _.keyBy(props.project.boards, 'id');
        this.statesMap = _.keyBy(props.project.states, 'id');
        this.queuesMap = _.keyBy(props.queues, 'id');
        this.queuesGroupedByBoardId = _.groupBy(props.queues, 'boardId');
    }

    onQueueMouseEnter(event) {
        let queueId = utils.getValueAtPathOfClosestParent(event.currentTarget, 'dataset.queueId');
        let queue = this.queuesMap[queueId];
        this.props.delegate.queuesPanelDidHighlightQueue(queue);
    }

    onQueueMouseLeave() {
        this.props.delegate.queuesPanelDidClearHighlight();
    }

    onQueueClick(event) {
        let queueId = utils.getValueAtPathOfClosestParent(event.currentTarget, 'dataset.queueId');
        let queue = this.queuesMap[queueId];
        this.props.delegate.queuesPanelDidSelectQueue(queue);
    }

    onQueueEdit(event) {
        let queueId = utils.getValueAtPathOfClosestParent(event.currentTarget, 'dataset.queueId');
        let queue = this.queuesMap[queueId];
        OverlayController.showOverlay({
            component: <QueueDialog queue={queue} projectId={this.props.project.id} />
        });
    }

    onQueueCreate() {
        let stateIdsIncluded = this.stateIdsIncluded();
        let stateIdsExcluded = this.stateIdsExcluded();
        OverlayController.showOverlay({
            component: <QueueDialog stateIdsIncluded={stateIdsIncluded} stateIdsExcluded={stateIdsExcluded} projectId={this.props.project.id} onSaveSuccess={(queue) => {
                this.props.delegate.queuesPanelDidCreateQueue(queue);
            }} />
        });
    }

    getQueueIncludedStates(queue) {
        if (queue.itemFilter.excludeAllStates) {
            return <b>None</b>;
        }
        return this.getQueueStateListFromStateIds(queue.itemFilter.stateIdsIncluded, 'included');
    }

    getQueueExcludedStates(queue) {
        if (queue.itemFilter.excludeAllStates) {
            return <b>All</b>;
        }
        return this.getQueueStateListFromStateIds(queue.itemFilter.stateIdsExcluded, 'excluded');
    }

    getQueueStateListFromStateIds(stateIds, filterType) {
        if (!stateIds.length) {
            if (filterType === 'included') {
                return <b>Any</b>;
            } else if (filterType === 'excluded') {
                return <b>None</b>;
            }
        }
        let stateNames = stateIds.map(stateId => <b key={stateId}>{this.statesMap[stateId].name}</b>);
        let result = [stateNames[0]];
        for (let i = 1; i < stateNames.length; i++) {
            result.push(', ', stateNames[i]);
        }
        return result;
    }

    isCreateQueueSelected() {
        return this.props.selection && this.props.selection.type === 'states';
    }

    stateIdsIncluded() {
        return this.props.selection.states.filter(state => state.filterType === 'included').map(state => state.id);
    }

    stateIdsExcluded() {
        return this.props.selection.states.filter(state => state.filterType === 'excluded').map(state => state.id);
    }

    renderQueues() {
        let addQueueListItem = null;
        if (this.isCreateQueueSelected()) {
            let includedStates = this.getQueueStateListFromStateIds(this.stateIdsIncluded(), 'included');
            let excludedStates = this.getQueueStateListFromStateIds(this.stateIdsExcluded(), 'excluded');
            addQueueListItem = (
                <li key="addQueuePlaceholder" className="materialListItem threeLine selected">
                    <div className="materialListItemText">
                        <div className="materialListItemPrimaryText">Create Queue</div>
                        <div className="materialListItemSecondaryText truncate-ellipsis">Incl. States: {includedStates}</div>
                        <div className="materialListItemSecondaryText truncate-ellipsis">Excl. States: {excludedStates}</div>
                    </div>
                    <IconButton className="materialListItemRightIcon" onClick={this.onQueueCreate}><AddIcon/></IconButton>
                </li>
            );
        } else {
            addQueueListItem = (
                <li key='addQueuePlaceholder' className="materialListItem threeLine">
                    <div className="materialListItemText">
                        <div className="materialListItemPrimaryText">
                            Select states in the diagram to create a queue with state filters.
                            Hold <b>Shift</b> to select multiple states, hold <b>Alt</b> to exclude state.
                        </div>
                    </div>
                </li>
            );
        }
        let queueItems = this.queueItems();
        queueItems.unshift(addQueueListItem);
        return <ul className="materialList dense queuesPanelList">{queueItems}</ul>
    }

    queueItems() {
        return _.flatMap(this.props.project.boards, board => {
            let queues = this.queuesGroupedByBoardId[board.id];
            if (!queues) {
                return [];
            }
            let boardHeader = (
                <li key={board.id} className="materialListItem materialListSubheader">
                    <div className="materialListItemText">{board.name}</div>
                </li>
            );
            return [boardHeader].concat(queues.map(queue => {
                let includedStates = this.getQueueIncludedStates(queue);
                let excludedStates = this.getQueueExcludedStates(queue);
                return (
                    <li key={queue.id} className="materialListItem threeLine"
                        data-queue-id={queue.id}
                        onClick={this.onQueueClick}
                        onMouseEnter={this.onQueueMouseEnter}
                        onMouseLeave={this.onQueueMouseLeave}>
                        <div className="materialListItemText">
                            <div className="materialListItemPrimaryText">{queue.name}</div>
                            <div className="materialListItemSecondaryText">Board: <b>{this.boardsMap[queue.boardId].name}</b></div>
                            <div className="materialListItemSecondaryText truncate-ellipsis">Incl. States: {includedStates}</div>
                            <div className="materialListItemSecondaryText truncate-ellipsis">Excl. States: {excludedStates}</div>
                        </div>
                        <IconButton className="materialListItemRightIcon" onClick={this.onQueueEdit}><EditIcon/></IconButton>
                    </li>
                );
            }));
        });
    }

    render() {
        return (
            <div className="queuesPanel">
                {this.renderQueues()}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/diagram/QueuesPanel.js